import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Phone, MessageCircle, Clock, ArrowRight, CalendarCheck } from "lucide-react";
import { Seo } from "../components/Seo";

const STEPS = [
  {
    icon: Clock,
    title: "We review your details",
    description: "Our team checks your postcode, property size and preferred date to confirm availability.",
  },
  {
    icon: CalendarCheck,
    title: "You receive a fixed quote",
    description: "We send a fixed-price quote — no hidden fees, no on-the-day surprises — usually within a couple of hours.",
  },
  {
    icon: CheckCircle,
    title: "Confirm and we book the slot",
    description: "Reply to confirm and your slot is locked in. You'll get a reminder the day before your clean.",
  },
];

const RequestReceived = () => {
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", { send_to: "AW-18227269510" });
    }
  }, []);

  return (
    <>
    <Seo
      title="Request Received — Prime Tenancy Cleaning"
      description="Your quote request has been received. One of our operators will send your fixed-price quote shortly."
    />

    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {/* Check icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            <span className="inline-block bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              Quote Request Sent
            </span>

            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              Request Received!
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed max-w-lg mx-auto">
              Thank you for contacting <strong>Prime Tenancy Cleaning</strong>. One of our
              operators will review your details and send you a fixed-price quote — usually
              within a couple of hours during business hours.
            </p>
          </motion.div>

          {/* What happens next */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-navy mb-6 text-center">
              What happens next
            </h2>
            <div className="space-y-6">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-navy/5 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-gold-dark">0{i + 1}</span>
                        <h3 className="font-semibold text-navy text-sm">{step.title}</h3>
                      </div>
                      <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Urgent contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="bg-navy rounded-2xl p-6 md:p-8 mb-6 text-white text-center"
          >
            <p className="text-slate-300 text-sm mb-4">
              Need your clean booked urgently? Reach us directly:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+442080363586"
                className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-5 py-3 rounded-xl transition-colors"
              >
                <Phone className="w-4 h-4" />
                020 8036 3586
              </a>
              <a
                href="https://wa.me/447346006547"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
            </div>
          </motion.div>

          {/* Back to home */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-center"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal hover:text-navy font-semibold transition-colors"
            >
              Back to home
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default RequestReceived;
